<script setup lang="ts">
import { computed } from 'vue';
import type { HitRecord } from './caseDetail';
import CopyButton from './CopyButton.vue';

const props = defineProps<{ record: HitRecord }>();

const isEmpty = !(
  props.record.url ||
  props.record.body?.length ||
  props.record.snippet ||
  props.record.fields?.length
);

const articleText = computed(() =>
  props.record.body?.length ? props.record.body.join('\n\n') : (props.record.snippet ?? '')
);
</script>

<template>
  <div class="flex flex-col gap-3" data-test="record-reader-content">
    <section v-if="record.url" class="rounded-xl border border-gray-200 bg-white shadow-sm">
      <div class="flex items-center justify-between border-b border-gray-100 px-4 py-2.5">
        <span class="text-[11px] font-semibold tracking-wide text-gray-400 uppercase">Sources</span>
        <span class="text-xs text-gray-400">1 source</span>
      </div>
      <div class="flex flex-col gap-2 px-4 py-3">
        <span class="flex items-baseline gap-1.5 font-mono text-xs break-all text-gray-500">
          {{ record.url }}
          <CopyButton :value="record.url ?? ''" label="source URL" />
        </span>
        <a
          :href="record.url"
          target="_blank"
          rel="noopener noreferrer"
          class="mt-1 w-fit rounded-md border border-gray-300 px-3 py-1.5 text-xs font-semibold text-gray-600 hover:bg-gray-50"
        >
          Open source ↗
        </a>
      </div>
    </section>

    <section
      v-if="record.body?.length || record.snippet"
      class="rounded-xl border border-gray-200 bg-white p-4 shadow-sm"
    >
      <div class="mb-2 flex justify-end">
        <CopyButton :value="articleText" label="article text" />
      </div>
      <p
        v-for="paragraph in record.body ?? []"
        :key="paragraph"
        class="text-sm leading-relaxed break-words whitespace-pre-wrap text-gray-700 not-last:mb-3"
      >
        {{ paragraph }}
      </p>
      <p
        v-if="record.snippet && !record.body?.length"
        class="text-sm leading-relaxed break-words whitespace-pre-wrap text-gray-700"
      >
        {{ record.snippet }}
      </p>
    </section>

    <section
      v-if="record.fields?.length"
      class="rounded-xl border border-gray-200 bg-white shadow-sm"
    >
      <div class="border-b border-gray-100 px-4 py-2.5">
        <span class="text-[11px] font-semibold tracking-wide text-gray-400 uppercase">Details</span>
      </div>
      <dl class="flex flex-col px-4 text-sm">
        <div
          v-for="field in record.fields"
          :key="field.k"
          class="flex items-start gap-2 border-t border-gray-100 py-1.5 first:border-t-0"
        >
          <dt class="w-32 flex-none text-xs tracking-wide break-words text-gray-400 uppercase">
            {{ field.k }}
          </dt>
          <dd class="min-w-0 flex-1 [overflow-wrap:anywhere] whitespace-pre-line text-gray-900">
            {{ field.v
            }}<CopyButton :value="field.v" :label="field.k" class="ml-1.5 align-middle" />
          </dd>
        </div>
      </dl>
    </section>

    <p v-if="isEmpty" class="text-sm text-gray-400">No further detail on file for this record.</p>
  </div>
</template>
